import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, LayoutGrid, Loader2, FileText, LogOut, HelpCircle } from "lucide-react";
import { getThemeAssets } from "@/utils/theme";
import { useDeliveryHub } from "@/contexts/DeliveryHubContext";

interface User {
  userName?: string;
  userEmail?: string;
  profile_pic?: string;
}

const DeliverHubSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const themeAssets = getThemeAssets();
  const { schools, loading } = useDeliveryHub();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const appSwitcherRef = useRef<HTMLDivElement>(null);

  // Close app switcher when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (appSwitcherRef.current && !appSwitcherRef.current.contains(event.target as Node)) {
        setShowAppSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get logged-in user details
  useEffect(() => {
    const userStr = localStorage.getItem('crm_user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      const userDetails = userData?.data?.data || userData?.data || userData;
      setUser({
        userName: userDetails?.userName || userDetails?.name || 'User',
        userEmail: userDetails?.userEmail || userDetails?.email || '',
        profile_pic: userDetails?.profile_pic || '',
      });
    }
  }, []);

  const getUserInitials = () => {
    if (!user?.userName) return 'U';
    const names = user.userName.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return user.userName.substring(0, 2).toUpperCase();
  };

  const getSchoolInitial = (name: string) => {
    return name?.charAt(0)?.toUpperCase() || 'S';
  };

  const currentSchoolId = location.pathname.match(/\/delivery-hub\/schools\/(\d+)/)?.[1];

  const filteredSchools = schools.filter((school) =>
    school.slate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    school.city_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    school.area_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSchoolClick = (schoolId: number) => {
    navigate(`/delivery-hub/schools/${schoolId}`);
  };

  const handleLogout = () => {
    navigate('/logout');
  };

  return (
    <div className="w-64 bg-brandColor text-white flex flex-col h-full">
      {/* Header with App Switcher Icon + Logo */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          {/* App Switcher Icon */}
          <div className="relative" ref={appSwitcherRef}>
            <button
              onClick={() => setShowAppSwitcher(!showAppSwitcher)}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              title="Switch Apps"
            >
              <LayoutGrid size={20} className="text-white" />
            </button>

            {/* App Switcher Dropdown */}
            {showAppSwitcher && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Switch App
                </div>
                <Link
                  to="/dashboard"
                  onClick={() => setShowAppSwitcher(false)}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
                    <LayoutGrid size={18} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 text-sm">CRM</div>
                    <div className="text-xs text-gray-500">Lead Management</div>
                  </div>
                </Link>
                <div className="flex items-center gap-3 px-3 py-2.5 bg-brand/10">
                  <div className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
                    <LayoutGrid size={18} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 text-sm">DeliveryHub</div>
                    <div className="text-xs text-gray-500">Deliverable Admin Portal</div>
                  </div>
                  <span className="text-brand text-xs font-medium">Active</span>
                </div>
                <a
                  href={import.meta.env.VITE_BLOG_URL || 'http://localhost:5003'}
                  onClick={() => setShowAppSwitcher(false)}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
                    <FileText size={18} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 text-sm">Blog</div>
                    <div className="text-xs text-gray-500">Content Management</div>
                  </div>
                </a>
              </div>
            )}
          </div>

          {/* Logo + Product Name */}
          <Link to="/delivery-hub" className="flex-1">
            <img
              src={themeAssets.loginLogo}
              alt={`${themeAssets.brandName} Logo`}
              className="h-7 object-contain"
            />
            <span className="text-[10px] text-white/50 mt-0.5 block">DeliveryHub</span>
          </Link>
        </div>
      </div>

      {/* Section links */}
      <nav className="px-2 py-2 border-b border-white/10">
        {[
          { to: "/delivery-hub", label: "Deliverables", icon: FileText, active: location.pathname === "/delivery-hub" },
          { to: "/delivery-hub/queries", label: "Queries", icon: HelpCircle, active: location.pathname === "/delivery-hub/queries" },
        ].map(({ to, label, icon: Icon, active }) => (
          <Link
            key={to}
            to={to}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              active ? "bg-white/20 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            <Icon size={16} />
            {label}
          </Link>
        ))}
      </nav>

      {/* Schools Section */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="px-4 py-3 flex items-center justify-between">
          <span className="text-xs font-medium text-white/60 uppercase tracking-wider">
            Schools ({schools.length})
          </span>
          {/* Total attention badge */}
          {schools.reduce((sum, s) => sum + (s.attention_count || 0), 0) > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
              {schools.reduce((sum, s) => sum + (s.attention_count || 0), 0)} needs attention
            </span>
          )}
        </div>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
            <input
              type="text"
              placeholder="Search schools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg pl-9 pr-3 py-2 text-sm placeholder-white/50 focus:outline-none focus:border-white/40 transition-colors"
            />
          </div>
        </div>

        {/* Schools List - scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto px-2 dark-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-white/50" />
            </div>
          ) : filteredSchools.length === 0 ? (
            <div className="text-center text-white/50 text-sm py-4">
              {searchTerm ? "No schools found" : "No schools available"}
            </div>
          ) : (
            filteredSchools.map((school) => {
              const isActive = currentSchoolId === String(school.slate_id);
              return (
                <button
                  key={school.slate_id}
                  onClick={() => handleSchoolClick(school.slate_id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors text-left ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "hover:bg-white/10 text-white/70 hover:text-white"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium relative ${
                      isActive ? "bg-white/30" : "bg-white/20"
                    }`}
                  >
                    {getSchoolInitial(school.slate_name)}
                    {/* Attention badge */}
                    {(school.attention_count ?? 0) > 0 && (
                      <span
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                        title={`${school.revision_count || 0} revision${(school.revision_count || 0) !== 1 ? 's' : ''}, ${school.query_count || 0} quer${(school.query_count || 0) !== 1 ? 'ies' : 'y'}, ${school.unread_comment_count || 0} unread comment${(school.unread_comment_count || 0) !== 1 ? 's' : ''}`}
                      >
                        {(school.attention_count ?? 0) > 9 ? '9+' : school.attention_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="block truncate text-sm">{school.slate_name?.replace(/_/g, ' ')}</span>
                    {(school.city_name || school.area_name) && (
                      <span className="block truncate text-xs text-white/50">
                        {[school.area_name, school.city_name].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* User Section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center text-sm font-medium">
            {getUserInitials()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.userName || 'User'}</div>
            <div className="text-xs text-white/60 truncate">{user?.userEmail || ''}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <LogOut size={16} />
          Logout
        </button>
      </div>
    </div>
  );
};

export default DeliverHubSidebar;
